import { flowRight } from "lodash-es";
import { type StoreApi, type UseBoundStore, create } from "zustand";

export interface UseBlueprintInterface<P> {
	id?: string;
	blueprint:
		| BlueprintType<P>
		| (BlueprintType<P> | ((...args: any[]) => any))[];
	initialValues?: BlueprintPropsType<P>;
}

type StripPrefix<
	TPrefix extends string,
	T extends string,
> = T extends `${TPrefix}${infer R}` ? R : never;

type AutomaticallyDerivedValues<T, TPrefix extends string> = {
	[K in keyof T & string as StripPrefix<TPrefix, K>]: T[K] extends (
		...any
	) => unknown
		? ReturnType<T[K]>
		: T[K];
};

type Expand<T> = T extends infer O ? { [K in keyof O]: O[K] } : never;

type ProxyStore<P> = Expand<
	BlueprintPropsWithWaitType<P> &
		AutomaticallyDerivedValues<P, "_"> &
		StoreApi<BlueprintPropsWithWaitType<P>>
>;

const globalStore = new Map();

export const useBlueprint = <P,>({
	id = undefined,
	blueprint,
	initialValues = { wait: false } as BlueprintPropsType<P>,
}: UseBlueprintInterface<P>) => {
	const typedStore = globalStore as Map<
		string | undefined,
		UseBoundStore<StoreApi<BlueprintPropsType<P>>>
	>;

	if (!typedStore.has(id)) {
		const castBlueprintArray = [blueprint].flat();
		const creator = flowRight(...castBlueprintArray)(initialValues);
		typedStore.set(id, create<BlueprintPropsType<P>>(creator));
	}

	const store = typedStore.get(id);
	const isWaiting = store?.getState()?.wait;
	if (isWaiting && !initialValues?.wait) {
		store.setState({ ...initialValues, wait: false });
	}

	return new Proxy(store as object, {
		get(
			target: UseBoundStore<StoreApi<BlueprintPropsType<P>>>,
			prop,
			receiver,
		) {
			// const isWaiting = target((state) => state["wait"]);
			const attribute = target<P>((state) => state?.[prop]);
			const derivedAttr = target((state) => state?.[`_${String(prop)}`]);

			// TODO: An issue occurs with this wait pattern that returns undefined for all values while waiting
			// if the value being derived is using a hook, since it breaks the same number of hooks rule.
			// Should we consider allowing derived values to execute regardless of wait, but set everything else to undefined?

			// if (isWaiting && prop !== "wait") return undefined;

			if (
				typeof attribute !== "undefined" ||
				typeof derivedAttr !== "undefined"
			) {
				if (typeof derivedAttr !== "undefined") {
					return derivedAttr();
				}
				return attribute;
			}
			return Reflect.get(target, prop, receiver);
		},
	}) as ProxyStore<P>;
};

export const createBlueprint = <P,>(storeFn: BluePrintCreatorType<P>) => {
	return (initialValues: BlueprintPropsType<P>) =>
		(
			set: StoreApi<P>["setState"],
			get: StoreApi<BlueprintPropsWithWaitType<P>>["getState"],
		) => ({
			...storeFn(set, get),
			...initialValues,
		});
};

type BlueprintPropsWithWaitType<P> = P & { wait?: boolean };
type BlueprintPropsType<P> = Partial<BlueprintPropsWithWaitType<P>>;
type BluePrintCreatorType<P> = (
	set: StoreApi<P>["setState"],
	get: StoreApi<BlueprintPropsWithWaitType<P>>["getState"],
) => P;
export type BlueprintType<P> = (
	initialValues: BlueprintPropsType<P>,
) => BluePrintCreatorType<P>;
